import axios from 'axios'
import {
  BulkAvailabilityResponse,
  CheckAgeDataResponse,
  CheckDomainAvailabilityResponse,
  FreshDropsResponse,
  LookupWhoIsDetails,
  LookupWhoIsResponse,
  BrandMonitorResponse,
  BrandMonitorWatchlist,
  SearchAllRegisteredDomainsDetails,
  SearchAllRegisteredDomainsResponse,
  SubscribeBrandMonitorWatchlistItemDetails,
  SubscribeBrandMonitorWatchlistItemResponse,
  DomainSalesLookupWatchlist,
  DomainSalesLookupWatchlistDetails,
  DomainSalesLookupResponse,
  TaskProgressResponse,
  DomainBacklinksLookupResponse,
  DomainMonitorResponse,
  DomainChangelogResponse,
  SEOMetricsResponse,
  CreateSEOMetricsResponse,
  SEOMetricsChangeResponse,
  DomainSEOAnalysisResponse,
  BulkAllRegisteredDomainsResponse,
} from 'models/tools.interface'
import { BishopiBaseService } from './bishopi-base.service'

class ToolsService extends BishopiBaseService {
  getTaskProgress(taskId: string) {
    return axios.get<TaskProgressResponse>(
      this.getPath(`/celery-progress/${taskId}/`)
    )
  }

  searchAllRegisteredDomains(keywords: string[]) {
    return this.get<SearchAllRegisteredDomainsResponse>(
      this.getPath(`/lead_score/search?keywords=${keywords}`)
    )
  }

  lookupWhoIs(details: LookupWhoIsDetails) {
    return axios.post<LookupWhoIsResponse>(
      this.getPath('/domains/whois/'),
      details
    )
  }

  getDomainAge(domain: string) {
    return axios.post<CheckAgeDataResponse>(
      this.getPath(`/domains/check-age/`),
      { domain }
    )
  }

  getDomainAvailability(domains: string[]) {
    return axios.post<CheckDomainAvailabilityResponse['data']>(
      this.getPath(`/domains/availability/`),
      { domains: domains.join(', ') }
    )
  }

  getBrandMonitor(params, token?: string) {
    return this.get<BrandMonitorResponse>(
      this.getPath(`/brand_monitor/?${params}`),
      token
    )
  }

  getSEOMetrics(params, token?: string) {
    return this.get<SEOMetricsResponse>(
      this.getPath(`/seo_monitor/?${params}/`),
      token
    )
  }

  getSEOMetricsChanges(domain, token?: string) {
    return this.get<SEOMetricsChangeResponse>(
      this.getPath(`/seo_monitor/changes/${domain}/`),
      token
    )
  }

  getSEOMetricsHistory(domain, dateFilter: string[], token?: string) {
    return this.get<SEOMetricsChangeResponse>(
      this.getPath(`/seo_monitor/history/${domain}/${dateFilter.join(',')}/`),
      token
    )
  }
  getBrandMonitorWatchlist() {
    return axios.get<BrandMonitorWatchlist>(
      this.getPath('/brand_monitor/brands/')
    )
  }

  deleteBrandMonitorWatchlistItem(id: number) {
    return axios.delete(this.getPath(`/brand_monitor/brands/details/${id}/`))
  }

  deleteSEOMetrics(id: number) {
    return axios.delete(this.getPath(`/seo_monitor/details/${id}/`))
  }

  subscribeBrandMonitorWatchlistItem(
    details: SubscribeBrandMonitorWatchlistItemDetails
  ) {
    return axios.post<SubscribeBrandMonitorWatchlistItemResponse>(
      this.getPath('/brand_monitor/brands/create/'),
      details
    )
  }

  createSEOMetrics(domain: string) {
    return axios.post<CreateSEOMetricsResponse>(
      this.getPath('/seo_monitor/create/'),
      { domain_name: domain }
    )
  }

  getDomainSales(params: string, token?: string) {
    return this.get<DomainSalesLookupResponse>(
      this.getPath(`/domains/sales/?${params}`),
      token
    )
  }

  getDomainBacklinks(domain: string) {
    return axios.post<DomainBacklinksLookupResponse>(
      this.getPath(`/domains/domain-backlink-check/`),
      { domain }
    )
  }

  getDomainSEOAnalysis(domain: string) {
    return axios.get<DomainSEOAnalysisResponse>(
      this.getPath(`/domains/domain-seo-analysis/?domain=${domain}`)
    )
  }

  getDomainSalesLookupWatchlist() {
    return axios.get<DomainSalesLookupWatchlist>(this.getPath('/sales_alerts/'))
  }

  subscribeDomainSalesLookupWatchlist(
    details: DomainSalesLookupWatchlistDetails
  ) {
    return axios.post(this.getPath('/sales_alerts/create/'), details)
  }

  deleteDomainSalesLookupWatchlistItem(id: number) {
    return axios.delete(this.getPath(`/sales_alerts/details/${id}/`))
  }

  uploadBulkAvailability(formData: FormData) {
    return axios.post<BulkAvailabilityResponse>(
      this.getPath(`/domains/bulk-availability-check/`),
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
  }

  uploadBulkAllDomainsSearch(formData: FormData) {
    return axios.post<BulkAllRegisteredDomainsResponse>(
      this.getPath(`/lead_score/bulk/`),
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
  }

  getFreshDrops(params: string, token?: string) {
    return this.get<FreshDropsResponse>(
      this.getPath(`/domains/fresh-drops/?${params}`),
      token
    )
  }

  getDomainMonitor(params, token?: string) {
    return this.get<DomainMonitorResponse>(
      this.getPath(`/domain_monitor/?${params}/`),
      token
    )
  }

  createDomainMonitor(domains: string) {
    return axios.post(this.getPath('/domain_monitor/create/'), {
      domain_name: domains,
    })
  }

  deleteDomainMonitor(id: number) {
    return axios.delete(this.getPath(`/domain_monitor/details/${id}/`))
  }

  getDomainChangelog(domain: string) {
    return axios.get<DomainChangelogResponse>(
      this.getPath(`/domain_monitor/changes/${domain}/`)
    )
  }
}

export const toolsService = new ToolsService()
